import {getRepositoryContextCrumb} from '@github-ui/global-navigation/context-crumbs'
import {
  getDisplayedContext,
  navigateWithContextRefresh,
} from '@github-ui/global-navigation/navigate-with-context-refresh'
import {type RefObject, useCallback, useEffect} from 'react'

import {useLayoutBaseRepository} from '../stores/layout-selectors'

const ISSUE_OR_PULL_REQUEST_PATH = /^\/([^/]+)\/([^/]+)\/(pull|issues)\/\d+(?:\/|$)/
const SELECTED_LINK_BY_SEGMENT = {pull: 'repo_pulls', issues: 'repo_issues'} as const

/** The parts of a native or React click event used to decide whether to handle it. */
type LinkClickEvent = Pick<
  MouseEvent,
  'button' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey' | 'defaultPrevented' | 'preventDefault' | 'target'
>

/**
 * Returns a click handler for links to an issue or pull request in another repository. On a plain click it keeps the
 * navigation in the mounted React app and refreshes the global navigation to the destination repository, instead of
 * the full page load that a navigation to another repository otherwise needs.
 *
 * The handler only prevents the link's default when that navigation starts, so modified clicks, other links, and
 * navigations the app can't handle (including while `global_nav_context_refresh` is disabled) keep the link's
 * existing behavior. Pass `anchor` when it isn't the closest link to the event target.
 */
export function useCrossRepositoryLinkClick() {
  const baseRepository = useLayoutBaseRepository()
  const currentRepositoryNameWithOwner = `${baseRepository.ownerLogin}/${baseRepository.name}`

  return useCallback(
    (event: LinkClickEvent, anchor?: HTMLAnchorElement | null) => {
      let targetAnchor = anchor
      if (targetAnchor === undefined) {
        targetAnchor = getClosestAnchor(event.target)
      }
      if (!targetAnchor || !isPlainClick(event, targetAnchor)) return

      const destination = getIssueOrPullRequestDestination(targetAnchor.href)
      if (!destination) return
      if (destination.repositoryNameWithOwner.toLowerCase() === currentRepositoryNameWithOwner.toLowerCase()) return

      const sourceContext = getDisplayedContext(getRepositoryContextCrumb(currentRepositoryNameWithOwner), {})
      if (!sourceContext) return

      const navigated = navigateWithContextRefresh({
        sourceContext,
        destinationContext: {
          ...getRepositoryContextCrumb(destination.repositoryNameWithOwner),
          path: destination.path,
          selectedLink: destination.selectedLink,
        },
      })
      if (navigated) event.preventDefault()
    },
    [currentRepositoryNameWithOwner],
  )
}

/**
 * Handles clicks on links to an issue or pull request in another repository anywhere inside `containerRef`, such as
 * server-rendered comment bodies. The listener runs before Turbo's, which skips clicks whose default was prevented.
 */
export function useCrossRepositoryLinkInterception(containerRef: RefObject<HTMLElement | null>) {
  const handleClick = useCrossRepositoryLinkClick()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const listener = (event: MouseEvent) => handleClick(event)
    container.addEventListener('click', listener)
    return () => container.removeEventListener('click', listener)
  }, [containerRef, handleClick])
}

function getIssueOrPullRequestDestination(href: string) {
  let url: URL
  try {
    url = new URL(href, window.location.href)
  } catch {
    return undefined
  }
  if (url.origin !== window.location.origin) return undefined

  const match = ISSUE_OR_PULL_REQUEST_PATH.exec(url.pathname)
  if (!match) return undefined

  const [, owner, repo, segment] = match as unknown as [string, string, string, keyof typeof SELECTED_LINK_BY_SEGMENT]
  return {
    path: `${url.pathname}${url.search}${url.hash}`,
    repositoryNameWithOwner: `${owner}/${repo}`,
    selectedLink: SELECTED_LINK_BY_SEGMENT[segment],
  }
}

function getClosestAnchor(target: EventTarget | null): HTMLAnchorElement | null {
  return target instanceof Element ? target.closest('a') : null
}

function isPlainClick(event: LinkClickEvent, anchor: HTMLAnchorElement): boolean {
  const target = anchor.getAttribute('target')
  // Modified clicks open the link elsewhere, such as in a new tab, so they keep the link's default behavior.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !isModifiedClick &&
    (!target || target === '_self') &&
    !anchor.hasAttribute('download') &&
    anchor.closest('[data-turbo]')?.getAttribute('data-turbo') !== 'false'
  )
}
